import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import Logo from '../../components/common/Logo';
import { format } from 'date-fns';
import { Search, Download, Loader, Calendar, User, UserCheck, FileSpreadsheet, X } from 'lucide-react';
import AppointmentService from '../../services/appoinment.service';
import * as XLSX from 'xlsx';
import { getStatusLabel, getStatusBadgeClass } from '../../utils/appointmentUtils';
import { 
  formatDate, 
  formatTime, 
  getAppointmentDateTime, 
} from '../../utils/dateUtils';

const AdminAppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showExportOptions, setShowExportOptions] = useState(false);

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await AppointmentService.getAdminAppointments();
      setAppointments(data);
      setError(null);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Impossibile caricare gli appuntamenti');
    } finally {
      setLoading(false);
    }
  };

  // ⭐ FUNZIONE AGGIORNATA per determinare se un appuntamento può essere cancellato
  const canCancelAppointment = (appointment) => {
    const now = new Date();
    // ⭐ USA getAppointmentDateTime invece di concatenare manualmente
    const appointmentDateTime = getAppointmentDateTime(appointment);
    
    // Non può cancellare se:
    // 1. Appuntamento già completato
    // 2. Appuntamento già cancellato
    // 3. Appuntamento già svolto (nel passato)
    return !(
      appointment.status === 'completed' ||
      appointment.status === 'cancelled' ||
      appointmentDateTime < now
    );
  };

  const handleCancelAppointment = async (appointmentId, appointmentData) => {
    const confirmMessage = `Sei sicuro di voler cancellare l'appuntamento di ${appointmentData.patient_name} del ${formatDate(appointmentData.date)}?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await AppointmentService.updateStatus(appointmentId, 'cancelled');
        await loadAppointments();
        alert('Appuntamento cancellato con successo');
      } catch (err) {
        console.error('Errore nella cancellazione dell\'appuntamento:', err);
        alert('Non è stato possibile cancellare l\'appuntamento. Riprova più tardi.');
      }
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = 
      (appointment.patient_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (appointment.therapist_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (appointment.service_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || appointment.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // ⭐ FUNZIONE AGGIORNATA per export CSV
  const exportToCSV = () => {
    const headers = ['Data', 'Ora', 'Paziente', 'Professionista', 'Servizio', 'Prezzo', 'Stato'];
    
    const csvRows = [
      headers.join(','),
      ...filteredAppointments.map(appointment => {
        return [
          // ⭐ USA formatDate e formatTime invece di format(new Date(...))
          formatDate(appointment.date),
          formatTime(appointment.time),
          appointment.patient_name || '',
          appointment.therapist_name || '',
          appointment.service_name,
          appointment.price,
          getStatusLabel(appointment.status)
        ].join(',');
      })
    ];
    
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join('\n');
    const encodedUri = encodeURI(csvContent);
    
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `appuntamenti_${format(new Date(), 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  // ⭐ FUNZIONE AGGIORNATA per export Excel
  const exportToExcel = () => {
    const headers = ['Data', 'Ora', 'Paziente', 'Professionista', 'Servizio', 'Prezzo', 'Stato'];
    
    const data = [
      headers,
      ...filteredAppointments.map(appointment => [
        // ⭐ USA formatDate e formatTime invece di format(new Date(...))
        formatDate(appointment.date),
        formatTime(appointment.time),
        appointment.patient_name || '',
        appointment.therapist_name || '',
        appointment.service_name,
        appointment.price,
        getStatusLabel(appointment.status)
      ])
    ];
    
    // Crea un nuovo workbook
    const wb = XLSX.utils.book_new();
    
    // Crea un worksheet dai dati
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Aggiungi il worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Appuntamenti');
    
    // Configura larghezze colonne
    const colWidths = [
      { wch: 12 },  // Data
      { wch: 8 },   // Ora
      { wch: 20 },  // Paziente
      { wch: 20 },  // Professionista
      { wch: 25 },  // Servizio
      { wch: 10 },  // Prezzo
      { wch: 20 },  // Stato
    ];
    ws['!cols'] = colWidths;
    
    // Genera il file e avvia il download
    XLSX.writeFile(wb, `appuntamenti_${format(new Date(), 'yyyyMMdd')}.xlsx`);
    setShowExportOptions(false);
  };

  return (
    <div className="flex min-h-screen">
      <div className="w-64 bg-white shadow-md">
        <div className="p-4">
          <Logo />
        </div>
        <AdminSidebar />
      </div>

      <div className="flex-1 p-8 bg-gray-50">
        <header className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Gestione Appuntamenti</h1>
              <p className="text-gray-600">Monitoraggio di tutti gli appuntamenti della piattaforma</p>
            </div>
            <div className="relative">
              <button 
                onClick={() => setShowExportOptions(!showExportOptions)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Esporta
              </button>
              
              {showExportOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={exportToCSV}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Esporta CSV
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Esporta Excel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-4 flex flex-wrap gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Cerca per paziente, terapeuta o servizio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 border rounded-lg"
              />
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              className="p-3 border rounded-lg min-w-32"
            >
              <option value="all">Tutti gli stati</option>
              <option value="confirmed">Confermati</option>
              <option value="suggested">Suggeriti</option>
              <option value="completed">Completati</option>
              <option value="cancelled">Cancellati</option>
              <option value="pending_payment">In attesa di pagamento</option>
            </select>
          </div>
        </header>

        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-4">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Appuntamenti totali</p>
                <p className="text-xl font-semibold">{filteredAppointments.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 mr-4">
                <UserCheck className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Appuntamenti completati</p>
                <p className="text-xl font-semibold">
                  {appointments.filter(a => a.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 mr-4">
                <User className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pazienti unici</p>
                <p className="text-xl font-semibold">
                  {new Set(appointments.map(a => a.patient_id)).size}
                </p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md text-red-700">
            {error}
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun appuntamento trovato
            </h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? "Nessun appuntamento corrisponde ai criteri di ricerca"
                : "Non ci sono ancora appuntamenti registrati nel sistema"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paziente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Professionista
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Servizio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prezzo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAppointments.map((appointment) => (
                    <tr key={appointment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {/* ⭐ USA formatDate invece di format(new Date(...)) */}
                        {formatDate(appointment.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {/* ⭐ USA formatTime invece di format(new Date(...)) */}
                        {appointment.time ? formatTime(appointment.time) : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {appointment.patient_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {appointment.therapist_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {appointment.service_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium">
                        €{appointment.price}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(appointment.status)}`}>
                          {getStatusLabel(appointment.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {canCancelAppointment(appointment) ? (
                          <button
                            onClick={() => handleCancelAppointment(appointment.id, appointment)}
                            className="text-red-600 hover:text-red-900 flex items-center"
                            title="Cancella appuntamento"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancella
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            {appointment.status === 'completed' ? 'Completato' :
                             appointment.status === 'cancelled' ? 'Già cancellato' :
                             'Non cancellabile'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Informazioni sui criteri di cancellazione */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Calendar className="w-5 h-5 text-blue-600 mt-0.5" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Criteri di Cancellazione
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  È possibile cancellare solo appuntamenti che non sono ancora stati completati, 
                  già cancellati o che non si sono ancora svolti.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAppointmentsPage;